class MegaMenu extends HTMLElement {
  static CONSTANTS = {
    CLASSES: {
      OPEN: "desktop-menu-open",
      CLOSING: "desktop-menu-closing",
      ACTIVE: "active",
    },
    DURATIONS: {
      TRANSITION: 300,
      LEAVE_DELAY: 100,
      OPEN_DELAY: 20,
    },
  };

  constructor() {
    super();
    this.timer = null;
    this.closeTimer = null;
    this.leaveTimer = null;
    this.items = this.querySelectorAll(":scope > .header-menu.mega");
    this.toggles = this.querySelectorAll(":scope > .header-menu.mega > summary");
    this.menus = this.querySelectorAll(".header-menu-content");
    this.onBodyClickEvent = this.onBodyClick.bind(this);
    this.init();
  }

  init() {
    const isClickTrigger = this.dataset.trigger === "click";
    
    this.toggles.forEach((toggle) => {
      if (!toggle.querySelector(".menu-caret")) {
        toggle.setAttribute("role", "button");
        toggle.setAttribute("aria-haspopup", "true");
        toggle.setAttribute("aria-expanded", "false");
      }
      
      if (isClickTrigger) {
        toggle.addEventListener("click", this.handleTriggerClick.bind(this));
      } else {
        toggle.addEventListener("click", this.handleTriggerClickWithBlock.bind(this));
        toggle.addEventListener("mouseenter", this.openMenu.bind(this));
      }

      // Accessibility: Keydown listener on the summary toggle
      toggle.addEventListener("keydown", this.handleKeydown.bind(this));
    });

    this.items.forEach((item) => {
      // Accessibility: Close menu when focus moves outside the details block
      item.addEventListener("focusout", this.handleFocusOut.bind(this));

      if (!isClickTrigger) {
        item.addEventListener("mouseleave", this.handleMenuMouseLeave.bind(this));
        item.addEventListener("mouseenter", this.handleMenuMouseEnter.bind(this));
      }
    });

    if (!isClickTrigger) {
      this.menus.forEach((menu) => {
        const overlay = menu.querySelector(".modal-overlay");
        if (overlay) {
          overlay.addEventListener("mouseenter", this.handleMenuMouseLeave.bind(this));
        }
      });
    }
  }

  openMenu(event) {
    const details = event.target.closest("details");
    if (!details) return;
    this.openMenuWithDetails(details);
  }

  openMenuWithDetails(details) {
    this.clearAllTimers();
    this.detailsContainer = details;
    
    details.setAttribute("open", true);
    const summary = details.querySelector("summary .menu-caret, summary");
    if (summary) summary.setAttribute("aria-expanded", "true");
    
    document.body.classList.add(MegaMenu.CONSTANTS.CLASSES.OPEN);
    document.body.classList.remove(MegaMenu.CONSTANTS.CLASSES.CLOSING);
    
    setTimeout(() => {
      if (details === this.detailsContainer) {
        details.classList.add(MegaMenu.CONSTANTS.CLASSES.ACTIVE);
      }
    }, MegaMenu.CONSTANTS.DURATIONS.OPEN_DELAY);

    this.items.forEach((item) => {
      if (
        item !== details &&
        (item.classList.contains(MegaMenu.CONSTANTS.CLASSES.ACTIVE) || item.hasAttribute("open"))
      ) {
        item.classList.remove(MegaMenu.CONSTANTS.CLASSES.ACTIVE);
        item.removeAttribute("open");
        const s = item.querySelector("summary .menu-caret, summary");
        if (s) s.setAttribute("aria-expanded", "false");
      }
    });
    
    document.addEventListener("click", this.onBodyClickEvent);
    document.addEventListener("touchstart", this.onBodyClickEvent, { passive: true });
  }

  closeMenu(trapFocus = true) {
    if (!this.detailsContainer) return;
    if (trapFocus && typeof removeTrapFocus === "function") {
      removeTrapFocus(this.summaryToggle);
    }
    this.closeMenuWithDetails(this.detailsContainer);
  }

  closeMenuWithDetails(details) {
    details.classList.remove(MegaMenu.CONSTANTS.CLASSES.ACTIVE);
    const summary = details.querySelector("summary .menu-caret, summary");
    if (summary) summary.setAttribute("aria-expanded", "false");
    
    document.body.classList.add(MegaMenu.CONSTANTS.CLASSES.CLOSING);
    
    this.closeTimer = setTimeout(() => {
      details.removeAttribute("open");
      if (this.detailsContainer === details) {
        document.body.classList.remove(MegaMenu.CONSTANTS.CLASSES.OPEN);
        document.body.classList.remove(MegaMenu.CONSTANTS.CLASSES.CLOSING);
      }
    }, MegaMenu.CONSTANTS.DURATIONS.TRANSITION);
    
    document.removeEventListener("click", this.onBodyClickEvent);
    document.removeEventListener("touchstart", this.onBodyClickEvent);
  }

  handleMenuMouseLeave(event) {
    this.clearAllTimers();
    this.leaveTimer = setTimeout(() => {
      this.menuProcessClose(event);
    }, MegaMenu.CONSTANTS.DURATIONS.LEAVE_DELAY);
  }

  menuProcessClose(event) {
    this.clearAllTimers();
    const details = event.target.closest("details");
    if (!details) return;
    this.closeMenuWithDetails(details);
  }

  handleMenuMouseEnter() {
    this.clearAllTimers();
    document.body.classList.remove(MegaMenu.CONSTANTS.CLASSES.CLOSING);
  }

  clearAllTimers() {
    if (this.timer) clearTimeout(this.timer);
    if (this.closeTimer) clearTimeout(this.closeTimer);
    if (this.leaveTimer) clearTimeout(this.leaveTimer);
  }

  handleTriggerClickWithBlock(event) {
    const href = event.currentTarget.dataset.href;
    if (href && !event.target.closest(".menu-caret")) {
      if (event.target.closest("a")) return;
      event.preventDefault();
      window.location.href = href;
      return;
    }
    event.preventDefault();
    this.detailsContainer = event.target.closest("details");
    if (this.detailsContainer.hasAttribute("open")) {
      this.menuProcessClose(event);
    } else {
      this.openMenuWithDetails(this.detailsContainer);
      if (event.target.closest(".menu-caret") && event.detail === 0) {
        this.focusFirstItem(this.detailsContainer);
      }
    }
  }

  handleTriggerClick(event) {
    event.preventDefault();
    const details = event.target.closest("details");
    if (details.hasAttribute("open")) {
      this.detailsContainer = details;
      this.closeMenu();
    } else {
      this.openMenuWithDetails(details);
    }
  }

  onBodyClick(event) {
    const isOutside = !this.contains(event.target) || event.target.classList.contains("modal-overlay");
    if (isOutside) {
      this.closeMenu(false);
    }
  }

  // Accessibility: Handle keyboard keys (Escape, ArrowDown, Space, Enter)
  handleKeydown(event) {
    const details = event.target.closest("details");
    if (!details) return;

    const summary = details.querySelector("summary");
    const link = summary.querySelector("a") || summary.dataset.href;

    if (event.key === "Escape") {
      event.preventDefault();
      this.closeMenuWithDetails(details);
      (summary.querySelector(".menu-caret") || summary).focus();
    } else if (event.key === "ArrowDown" || ((event.key === " " || event.key === "Enter") && !link)) {
      // Space, ArrowDown, or Enter (when summary has no internal anchor link) should toggle menu
      event.preventDefault();
      if (!details.hasAttribute("open")) {
        this.openMenuWithDetails(details);
        // Focus first link in dropdown menu
        this.focusFirstItem(details);
      } else {
        this.closeMenuWithDetails(details);
      }
    }
  }

  focusFirstItem(details) {
    const firstFocusable = details.querySelector(".header-menu-content a, .header-menu-content button");
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 50);
    }
  }

  // Accessibility: Handle focusout on the details element
  handleFocusOut(event) {
    const details = event.currentTarget;
    setTimeout(() => {
      // If the focus has left the details element completely, close the menu
      if (!details.contains(document.activeElement)) {
        this.closeMenuWithDetails(details);
      }
    }, 50);
  }
}

customElements.define("mega-menu", MegaMenu);
